import React, { useEffect, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { DemoBookingModal } from "@/components/DemoBookingModal";

const DemoSection = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isFormLoaded) {
          loadHubSpotForm();
          setIsFormLoaded(true);
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isFormLoaded]);

  const loadHubSpotForm = () => {
    const existingScript = document.querySelector('script[src="//js.hsforms.net/forms/embed/v2.js"]');
    
    const createForm = () => {
      if ((window as any).hbspt) {
        (window as any).hbspt.forms.create({
          portalId: "99128",
          formId: "ea664590-de56-45ab-9719-4acf6b81ccc7",
          region: "na1",
          target: "#hubspot-form-section",
        });
      }
    };

    if (existingScript && (window as any).hbspt) {
      createForm();
    } else {
      const script = document.createElement('script');
      script.charset = 'utf-8';
      script.type = 'text/javascript';
      script.src = '//js.hsforms.net/forms/embed/v2.js';
      script.async = true;
      script.onload = () => createForm();
      document.head.appendChild(script);
    }
  };

  return (
    <>
      <section 
        ref={sectionRef}
        className="bg-gradient-to-br from-gray-50 to-white py-20 2xl:py-32 px-4 border-t border-gray-100"
      >
        <div className="container mx-auto max-w-7xl 2xl:max-w-[1600px] 2xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 2xl:gap-20 items-center">
            {/* Left Column - Demo Prompt */}
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl 2xl:text-6xl font-bold text-gray-900 leading-tight">
                Ready to Transform Your 
                <span className="block bg-gradient-to-r from-[#2563EB] to-[#1E40AF] bg-clip-text text-transparent">
                  Workplace Culture?
                </span>
              </h2>
              <p className="text-xl 2xl:text-2xl text-gray-600 leading-relaxed">
                See how Assembly can help you build a more engaged, recognized, and motivated team. 
                Book a personalized demo and discover the difference meaningful recognition makes.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#2563EB] rounded-full"></div>
                  <span className="text-gray-700">15-minute personalized walkthrough</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#2563EB] rounded-full"></div>
                  <span className="text-gray-700">See real ROI examples from similar companies</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#2563EB] rounded-full"></div>
                  <span className="text-gray-700">Get implementation timeline & pricing</span>
                </div>
              </div>
              <Button 
                onClick={() => setIsDemoModalOpen(true)}
                className="bg-[#2563EB] hover:bg-[#1E40AF] text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 lg:hidden"
                size="lg"
              >
                Book Your Demo Now
              </Button>
              <p className="text-sm text-gray-500 lg:hidden">
                No commitment required • Available for immediate setup
              </p>
            </div>

            {/* Right Column - HubSpot Form */}
            <div className="bg-white p-4 md:p-8 rounded-2xl shadow-xl border border-gray-100 hidden lg:block">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Book Your Demo
                </h3>
                <p className="text-gray-600">
                  Fill out the form and we'll be in touch
                </p>
              </div>
              
              <div 
                id="hubspot-form-section" 
                className="min-h-[400px] w-full"
              >
                {!isFormLoaded && (
                  <div className="text-center space-y-4 py-12">
                    <div className="w-12 h-12 mx-auto border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500">Loading form...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </>
  );
};

export default DemoSection;
